import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import aboutPoolSunset from "@/assets/about-pool-sunset.webp";
import aboutArrival from "@/assets/about-arrival.jpg";
import aboutDining from "@/assets/about-dining.jpg";
import aboutExteriorDining from "@/assets/about-exterior-dining.jpg";
import aboutWellness from "@/assets/about-wellness.webp";
import aboutCocktail from "@/assets/about-cocktail.png";
import aboutLattice from "@/assets/about-lattice.jpg";
import logoIcon from "@/assets/logo-icon.webp";
import logoIbiza from "@/assets/logo-ibiza.png";
import logoMarbella from "@/assets/logo-marbella.png";
import logoStTropez from "@/assets/logo-st-tropez.png";
import logoDubai from "@/assets/logo-dubai.png";
import logoParis from "@/assets/logo-paris.png";

const ease = [0.22, 1, 0.36, 1] as const;

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About Arzani Group — Our Story & Mission"
        description="Arzani Group curates luxury concierge experiences across Ibiza, Marbella, Saint-Tropez, Paris and Dubai."
        path="/about"
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-8 md:pb-12 bg-background">
        <div className="container-luxury">
          <motion.span
            className="font-label text-foreground/40 text-[10px] md:text-xs tracking-[0.2em] uppercase block mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease }}
          >
            About Arzani
          </motion.span>
          <motion.h1
            className="font-display text-3xl md:text-5xl lg:text-6xl text-foreground leading-[1.05] uppercase tracking-[0.15em] mb-12 md:mb-16"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.8, delay: 0.4, ease }}
          >
            The Arzani Story
          </motion.h1>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 0.6, ease }}
          >
            <img src={aboutPoolSunset} alt="Arzani Experience" className="w-full max-w-5xl h-[280px] md:h-[420px] object-cover" loading="lazy" />
          </motion.div>
        </div>
      </section>

      {/* Intro — founding story */}
      <section className="py-10 md:py-16 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img src={aboutArrival} alt="Arzani Experience" className="w-full h-[260px] md:h-[380px] object-cover" loading="lazy" />
            </motion.div>

            <motion.div
              className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                Arzani was founded from a clear understanding of how luxury destinations are best experienced — not through availability alone, but through structure, timing, and the way every element connects.
              </p>
              <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                Developed through years of direct exposure to destinations such as Ibiza, Saint-Tropez, Marbella, Paris, and Dubai, Arzani is built on real insight into how these environments operate at a high level. What began as a personal understanding of access, coordination, and local networks has evolved into a dedicated concierge service with a clear and defined standard.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 md:py-24 bg-background border-t border-border">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <motion.div
              className="lg:col-span-4 flex flex-col gap-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <div>
                <span className="font-label text-foreground/40 text-[10px] tracking-[0.2em] uppercase block mb-5">What We Do</span>
                <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                  Arzani is a luxury concierge and experience curation company, providing seamless access to high-end services across selected international destinations.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] mb-5">
                  We arrange private yachts, exceptional residences, prestige vehicles, and refined lifestyle services — including dining, nightlife, wellness, transport, and bespoke requests.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                  Each service is delivered through a carefully selected network of trusted partners, ensuring consistency, discretion, and quality across every destination.
                </p>
              </div>
              <img src={aboutCocktail} alt="Arzani Detail" className="w-full h-[160px] md:h-[200px] object-cover" loading="lazy" />
            </motion.div>

            <motion.div
              className="lg:col-span-7 lg:col-start-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.1, ease }}
            >
              <img src={aboutDining} alt="Arzani Dining" className="w-full h-[280px] md:h-[400px] object-cover" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Centered statement */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.p
            className="font-accent text-foreground/60 text-sm md:text-base lg:text-lg leading-[1.8] italic"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            Our role is not simply to provide options, but to structure complete experiences.
          </motion.p>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 md:py-32 bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img src={aboutLattice} alt="Arzani Detail" className="w-full h-[180px] md:h-[240px] object-cover" loading="lazy" />
            </motion.div>
            <motion.div
              className="lg:col-span-7 lg:col-start-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.5, delay: 0.1, ease }}
            >
              <span className="font-label text-primary-foreground/40 text-[10px] tracking-[0.2em] uppercase block mb-5">Our Approach</span>
              <p className="font-body text-primary-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                At Arzani, every request is approached with clarity and intention. We focus on understanding how each client prefers to move, spend time, and experience a destination. From there, every element is curated and arranged in advance — ensuring alignment across all services.
              </p>
              <p className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[1.9] mb-5">
                From a day on the water to a multi-day stay, every detail is handled with precision.
              </p>
              <p className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[1.9]">
                Our presence remains consistent, yet discreet — allowing the experience to feel effortless, while maintaining full control behind the scenes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* A Consistent Standard */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <span className="font-label text-foreground/40 text-[10px] tracking-[0.2em] uppercase block mb-5">A Consistent Standard</span>
              <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                Arzani operates across destinations including Ibiza, Saint-Tropez, Marbella, Paris, and Dubai — each with its own character, yet all delivered under a unified standard.
              </p>
              <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                Regardless of location, every experience follows the same principles.
              </p>
            </motion.div>
            <motion.div
              className="lg:col-span-6 lg:col-start-7"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img src={aboutExteriorDining} alt="Arzani Dining Experience" className="w-full h-[260px] md:h-[360px] object-cover" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 md:py-36 bg-background border-t border-border">
        <div className="container-luxury max-w-3xl mx-auto text-center">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-14">
            {[
              "Carefully selected",
              "Seamlessly coordinated",
              "Discreetly delivered",
              "Personally aligned",
            ].map((principle, i) => (
              <motion.div
                key={principle}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease }}
              >
                <div className="divider-subtle mx-auto mb-4" />
                <p className="font-label text-foreground/60 text-[9px] md:text-[10px] tracking-[0.2em] uppercase">
                  {principle}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.p
            className="font-body text-muted-foreground text-xs leading-[2] mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: 0.3, ease }}
          >
            This consistency allows us to maintain a high level of service across different environments, without compromising quality.
          </motion.p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 md:py-32 bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-6 lg:col-start-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.5, ease }}
            >
              <span className="font-label text-primary-foreground/40 text-[10px] tracking-[0.2em] uppercase block mb-5">Philosophy</span>
              <p className="font-body text-primary-foreground/70 text-xs md:text-sm leading-[1.9] mb-6">
                We believe that luxury is defined by the absence of friction.
              </p>
              <p className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[1.9] mb-6">
                Not by the number of options available, but by how effortlessly everything comes together.
              </p>
              <p className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[1.9]">
                By removing complexity, anticipating needs, and structuring each experience in advance, we allow our clients to focus entirely on their time — without interruption, delay, or unnecessary decisions.
              </p>
            </motion.div>

            <motion.div
              className="lg:col-span-4 lg:col-start-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <img src={aboutWellness} alt="Arzani Philosophy" className="w-full h-[200px] md:h-[280px] object-cover" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modern Approach */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            <span className="font-label text-foreground/40 text-[10px] tracking-[0.2em] uppercase block mb-5">A Modern Approach to Concierge</span>
            <p className="font-body text-foreground/60 text-xs md:text-sm leading-[2] mb-5">
              Arzani represents a more modern approach to concierge services.
            </p>
            <p className="font-accent text-foreground/50 text-sm md:text-base italic leading-[1.8]">
              Less transactional.<br />
              More considered.<br />
              Less about access alone,<br />
              and more about how everything is experienced.
            </p>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[2] mt-6">
              Built on real understanding, trusted networks, and a clear operational structure, Arzani continues to develop as a brand focused on delivering consistent, high-level experiences across the world's most sought-after destinations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing — 5 destination logos */}
      <section className="py-12 md:py-16 bg-background">
        <div className="container-luxury">
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16 lg:gap-20">
            {[
              { src: logoIbiza, alt: "Arzani Group Ibiza" },
              { src: logoMarbella, alt: "Arzani Group Marbella" },
              { src: logoStTropez, alt: "Arzani Group St. Tropez" },
              { src: logoDubai, alt: "Arzani Group Dubai" },
              { src: logoParis, alt: "Arzani Group Paris" },
            ].map((logo, i) => (
              <motion.img
                key={logo.alt}
                src={logo.src}
                alt={logo.alt}
                className="h-40 md:h-52 lg:h-64"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 0.5, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.1, ease }}
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
